import type React from 'react'
import Sidebar from '../Sidebar'
import { ScriptBox } from './ScriptBox'
import { useTranslation } from 'react-i18next'
import Icon from '../../common/Icon'
import type { Script } from '@/features/script/domain/models/script.ts'
import { useScriptStore } from '@/shared/store/script.store.ts'
import useSpeechDialogStore from '@/components/SpeechDialog/speech-dialog.store.ts'
import useUiStore from '@/shared/store/ui.store'
import { useShallow } from 'zustand/shallow'

const ScriptsBoxList: React.FC = () => {
  const { t } = useTranslation()
  const scripts = useScriptStore((state) => state.scripts)
  const { selectScript } = useSpeechDialogStore()
  const { setSpeechDialogOpen } = useUiStore(
    useShallow((state) => ({ setSpeechDialogOpen: state.setSpeechDialogOpen }))
  )

  const sortedScripts = [...(scripts ?? [])].sort((a, b) =>
    b.lastUpdate < a.lastUpdate ? -1 : 1
  )

  const hasScripts = sortedScripts.length > 0
  const onSelectScript = (scriptId: string) => {
    void selectScript(scriptId)
    setSpeechDialogOpen(true)
  }
  const onNewScript = () => {
    void selectScript()
    setSpeechDialogOpen(true)
  }
  return (
    <Sidebar title={'ls_sidebar_script_title'} className="bg-zinc-800">
      <div className="flex flex-col gap-6 p-6 pl-4">
        {/* Empty state */}
        {!hasScripts && (
          <div className="flex flex-col items-center justify-center pt-4 text-center">
            <div className="mb-5">
              <Icon name="chatBubble" width={48} height={48} />
            </div>
            <h2 className="mb-2 text-base font-bold text-white">
              {t('ls_sidebar_scripts_empty_title')}
            </h2>
            <p className="text-sm text-white">
              {t('ls_sidebar_scripts_empty_description')}
            </p>
          </div>
        )}
        {/* New script button */}
        <button
          type="button"
          className="flex h-[60px] cursor-pointer items-center justify-center gap-2 rounded bg-zinc-700 px-4 hover:bg-zinc-700/75"
          onClick={onNewScript}
        >
          <Icon name="add" width={20} height={20} />
          <span className="text-sm font-semibold">
            {t('ls_sidebar_new_script')}
          </span>
        </button>

        {hasScripts && (
          <div className="flex flex-col gap-5">
            {/* Script history section */}
            <div className="flex h-12 items-center gap-1 border-b border-b-white/10 px-2 text-center">
              <Icon name="clock" width={16} height={16} />
              <p className="text-sm text-white">
                {t('ls_sidebar_scripts_history_title')}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {/* Script list */}
              {sortedScripts.map((script: Script) => (
                <ScriptBox
                  key={script.id}
                  script={script}
                  onSelect={() => onSelectScript(script.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  )
}

export default ScriptsBoxList
